import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { slotService } from "@/services/slotService";
import { Slot } from "@/types/slot";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import SimpleCalendarView from "@/components/calendar/simple-calendar-view";
import { TimeRange } from "@/lib/calendar-types";
import { toast } from "@/components/ui/use-toast";
import { format, parseISO } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function PublicSlotBooking() {
  const { id } = useParams<{ id: string }>();
  const [slot, setSlot] = useState<Slot | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<TimeRange[]>([]);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);

  useEffect(() => {
    if (id) {
      fetchSlot();
    }
  }, [id]);

  const fetchSlot = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const { data, error } = await slotService.getSlot(id);
      if (error) throw error;
      setSlot(data);
    } catch (error) {
      console.error("Error fetching slot:", error);
      toast({
        title: "Error",
        description: "Failed to load slot details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSlotClick = (slot: Date) => {
    // When a slot is clicked, open the confirmation dialog
    setSelectedSlot(slot);
    setConfirmDialogOpen(true);
  };

  const handleConfirmSlot = () => {
    if (!selectedSlot) return;

    // Create a TimeRange from the selected slot
    const endDate = new Date(selectedSlot);
    endDate.setMinutes(endDate.getMinutes() + (slot?.time_limit || 30));

    const timeRange: TimeRange = {
      id: crypto.randomUUID(),
      startDate: selectedSlot,
      endDate: endDate,
      dayName: format(selectedSlot, "EEEE"),
    };

    // Set the selected time slot
    setSelectedTimeSlots([timeRange]);
    setConfirmDialogOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !slot || selectedTimeSlots.length === 0) return;

    setSubmitting(true);
    try {
      // Create the booking
      await slotService.createBooking(id, {
        name: `${formData.first_name} ${formData.last_name}`,
        email: formData.email,
        phone: formData.phone,
        status: "confirmed",
      });

      toast({
        title: "Success",
        description: "Your booking has been confirmed!",
      });

      // Reset form
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
      });
      setSelectedTimeSlots([]);
    } catch (error) {
      console.error("Error creating booking:", error);
      toast({
        title: "Error",
        description: "Failed to create booking",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!slot) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-[400px] rounded-none">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-2">Slot Not Found</h2>
            <p className="text-muted-foreground">
              The slot you're looking for doesn't exist or has been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 bg-gray-50">
      <div className="max-w-6xl mx-auto space-y-8">
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle>{slot.name || "Book Your Slot"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="border rounded-none p-4 bg-white">
                <h3 className="text-lg font-medium mb-4">
                  Select Your Time Slot
                </h3>
                <SimpleCalendarView
                  initialBookedRanges={
                    slot.segments?.map((segment, index) => ({
                      id: `${slot.id}-segment-${index}`,
                      title:
                        slot.name ||
                        `Slot (${slot.filled_seats}/${slot.capacity})`,
                      startDate: new Date(segment.start_time),
                      endDate: new Date(segment.end_time),
                      dayName: format(new Date(segment.start_time), "EEEE"),
                      bookedBy: `${slot.filled_seats}/${slot.capacity} booked`,
                    })) || []
                  }
                  slotDuration={slot.time_limit || 30}
                  selectedTimeSlots={selectedTimeSlots}
                  onRangesChange={setSelectedTimeSlots}
                  onSlotClick={handleSlotClick}
                />
              </div>

              <Dialog
                open={confirmDialogOpen}
                onOpenChange={setConfirmDialogOpen}
              >
                <DialogContent className="rounded-none">
                  <DialogHeader>
                    <DialogTitle>Confirm Time Slot</DialogTitle>
                    <DialogDescription>
                      {selectedSlot && (
                        <div className="py-4">
                          <p className="font-medium">
                            Would you like to book this time slot?
                          </p>
                          <div className="mt-2 p-3 bg-muted rounded-none">
                            <p>
                              <span className="font-medium">Date:</span>{" "}
                              {format(selectedSlot, "EEEE, MMMM d, yyyy")}
                            </p>
                            <p>
                              <span className="font-medium">Time:</span>{" "}
                              {format(selectedSlot, "h:mm a")}
                            </p>
                            {slot?.time_limit && (
                              <p>
                                <span className="font-medium">Duration:</span>{" "}
                                {slot.time_limit} minutes
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setConfirmDialogOpen(false)}
                      className="rounded-none"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirmSlot}
                      className="rounded-none"
                    >
                      Confirm Selection
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={submitting || selectedTimeSlots.length === 0}
                  className="rounded-none"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Confirming Booking...
                    </>
                  ) : (
                    "Confirm Booking"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
